import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, inject, OnDestroy, ProviderToken, ViewChild } from '@angular/core';
import { ButtonComponent } from '@components/shared/button/button.component';
import { ButtonType } from '@models/enums/components/Button/ButtonType.enum';
import { PortalService } from '@services/portal.service';
import Splide from '@splidejs/splide';
import anime from 'animejs';

interface CarouselData {
  index: number;
  images: string[];
  title: string;
  descriptions: string[];
}

const CAROUSEL_DATA = 'CarouselData' as unknown as ProviderToken<CarouselData>;

@Component({
    selector: 'app-benefits-fullscreen-carousel',
    imports: [
        ButtonComponent
    ],
    standalone: true,
    templateUrl: './benefits-fullscreen-carousel.component.html',
    styleUrl: './benefits-fullscreen-carousel.component.css'
})
export class BenefitsFullscreenCarouselComponent implements AfterViewInit, OnDestroy{

  public readonly carouselData = inject(CAROUSEL_DATA, { optional: true });
  private readonly portal: PortalService = inject(PortalService);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  // VIEW CHILD
  @ViewChild('benefitsFullscreen')
  public readonly container!: ElementRef<HTMLDivElement>;

  @ViewChild('carousel', {static: true, read: ElementRef})
  public readonly carousel!: ElementRef;

  // HOST LISTENERS
  @HostListener('window:keydown', ['$event'])
  protected closeFullScreen(event: KeyboardEvent): void {
    
    if (event.key === 'Escape') {
      event.preventDefault();
      this.toggleContainer(false);
    }

  }

  // COMPONENT STATE
  public splide!: Splide;
  public readonly btnType: ButtonType = ButtonType.Pill;
  private initFrame: number | null = null;
  private refreshFrame: number | null = null;



  // GETTERS
  public get title(): string {
    return this.carouselData?.title || '';
  }

  public get text(): string {
    return this.carouselData?.descriptions[this.splide?.index ?? 0] || '';
  }

  public get images(): string[] {
    return this.carouselData?.images || [];
  }

  public get index(): number {
    return this.splide ? this.splide.index + 1 : 0;
  }

  public get total(): number {
    return this.images.length;
  }

  // LIFECYCLE HOOKS
  public ngAfterViewInit(): void {
    this.initComponent();
  }

  public ngOnDestroy(): void {
    if(this.initFrame !== null) cancelAnimationFrame(this.initFrame);
    if(this.refreshFrame !== null) cancelAnimationFrame(this.refreshFrame);
    this.splide?.destroy();
  }

  public toggleContainer(show: boolean): void {

    anime({
      targets: this.container.nativeElement,
      opacity: show ? 1 : 0,
      duration: 300,
      easing: 'easeInOutQuad',
      complete: (): void => { if(!show) this.portal.resetPortal(); } 
    })

  }

  public refreshCarousel(): void {

    if(!this.splide) return;

    if(this.refreshFrame !== null) cancelAnimationFrame(this.refreshFrame);

    this.refreshFrame = requestAnimationFrame((): void => {
      this.refreshFrame = null;
      this.splide?.refresh();
      this.cdr.markForCheck();
    });

  }


  private setCarousel(): void {

    const start: number = Math.min(Math.max(this.carouselData?.index ?? 0, 0), Math.max(this.total - 1, 0));
     
    this.splide = new Splide(this.carousel.nativeElement, {
      type: 'loop',
      perPage: 1,
      perMove: 1,
      gap: '1rem',
      start,
      drag:       this.total === 1 ? false : true,
      pagination: this.total === 1 ? false : true,
      arrows:     this.total === 1 ? false : true,     
    });

    this.splide.on('mounted moved resized refreshed', (): void => this.cdr.markForCheck());
   
    this.splide.mount();
    
    this.refreshCarousel();

  }

  private initComponent(): void {

    this.initFrame = requestAnimationFrame((): void => {
      this.initFrame = null;
      this.setCarousel();
      this.toggleContainer(true);
    });

  }

  

}
